import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Stack, Typography } from '@mui/material';
import ConfigCard from './ConfigCard';
import { ChipsBox, ChipToggle, AvailableChip, SubLabel } from './_PdiHelpers';
import { useWebshopLanguages } from '../../WebshopLanguagesContext';
import { LanguageBar } from '../../_LanguageBar';

const ALL_LANGS = [
    { code: 'nl', label: 'Dutch'   },
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'French'  },
];

// Two ordered groups + an "available" pool. The schema splits on-page text
// fields from meta/add-on fields, since they're rendered in different blocks
// on the storefront.
const ALL_FIELDS = [
    { key: 'shortDescription',    i18nKey: 'cfg_pdi_dtl_short',          fallback: 'Short description'    },
    { key: 'longDescription',     i18nKey: 'cfg_pdi_dtl_long',           fallback: 'Long description'     },
    { key: 'marketingCopy',       i18nKey: 'cfg_pdi_dtl_marketing',      fallback: 'Marketing copy'       },
    { key: 'washInstructions',    i18nKey: 'cfg_pdi_dtl_wash',           fallback: 'Wash instructions'    },
    { key: 'seoDescription',      i18nKey: 'cfg_pdi_dtl_seo',            fallback: 'SEO description'      },
    { key: 'materialComposition', i18nKey: 'cfg_pdi_dtl_material_comp',  fallback: 'Material composition' },
    { key: 'careLabel',           i18nKey: 'cfg_pdi_dtl_care_label',     fallback: 'Care label'           },
    { key: 'origin',              i18nKey: 'cfg_pdi_dtl_origin',         fallback: 'Origin'               },
    { key: 'fitNotes',            i18nKey: 'cfg_pdi_dtl_fit_notes',      fallback: 'Fit notes'            },
];

// Default placement: which fields land in which group when nothing has been
// configured yet. Anything not listed defaults to AVAILABLE.
const DEFAULT_PLACEMENT = {
    shortDescription: 'page',
    longDescription:  'page',
    marketingCopy:    'page',
    washInstructions: 'meta',
};

const DescriptionTextsPerLanguageCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    // Only show language tabs the admin is allowed to edit (ADMIN_LANGUAGES).
    const { adminLanguages } = useWebshopLanguages();
    const LANGS = ALL_LANGS.filter(l => adminLanguages.includes(String(l.code).toUpperCase()));
    const [tab, setTab] = useState(0);
    const activeTab = (tab >= 0 && tab < LANGS.length) ? tab : 0;
    const lang = (LANGS[activeTab] || ALL_LANGS[0]).code;
    const langData = value[lang] || {};

    // Each field stores its placement: 'page' | 'meta' | 'available'.
    const placementOf = (k) => langData[k]?.placement || DEFAULT_PLACEMENT[k] || 'available';
    const setPlacement = (k, placement) => {
        if (!onChange) return;
        onChange(lang, { ...langData, [k]: { placement } });
    };

    const buildItems = (placement) => ALL_FIELDS.filter(f => placementOf(f.key) === placement);

    const pageItems = buildItems('page');
    const metaItems = buildItems('meta');
    const available = buildItems('available');

    return (
        <ConfigCard
            anchor="cfg-card-pdi-descriptions"
            title={tr('cfg_pdi_dtl_title', 'Product description texts per language')}
            description={tr('cfg_pdi_dtl_desc', 'Different languages may have different texts filled in. Pick which description fields to show on the product page, and in what order, for each language.')}
            theme={theme}
        >
            {/* This card has no backend mapping yet: the storefront's detail
                page reads a single CSV (SHOW_FIELDS_ON_DETAIL_PAGE, handled
                by ProductDetailsListCard) rather than a per-language
                placement object. Edits here change local state but are not
                included in the save payload — see WebshopConfigScreen's PDI
                save block. Flagged for the user so they don't expect persist. */}
            <Alert severity="info" sx={{ py: 0.5 }}>
                <Typography variant="body2">
                    {tr('cfg_pdi_dtl_planned', 'Not active yet — these per-language placements will be saved once the storefront supports per-language description toggles.')}
                </Typography>
            </Alert>

            <LanguageBar
                langs={LANGS}
                activeCode={lang}
                onSelect={(code) => setTab(LANGS.findIndex(l => l.code === code))}
                tr={tr}
            />

            <Stack spacing={2.5} sx={{ mt: 1.5 }}>
                {/* Page texts: ordered list + its own add row beneath */}
                <Box>
                    <SubLabel>{tr('cfg_pdi_dtl_on_page', 'Texts shown on the product page (in order)')}</SubLabel>
                    <ChipsBox>
                        {pageItems.map(it => (
                            <ChipToggle
                                key={it.key}
                                label={tr(it.i18nKey, it.fallback)}
                                onRemove={() => setPlacement(it.key, 'available')}
                            />
                        ))}
                    </ChipsBox>
                    {available.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                            {available.map(it => (
                                <AvailableChip
                                    key={it.key}
                                    label={tr(it.i18nKey, it.fallback)}
                                    onAdd={() => setPlacement(it.key, 'page')}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Meta texts: same available pool, but the + here adds into meta */}
                <Box>
                    <SubLabel>{tr('cfg_pdi_dtl_meta', 'Texts shown for meta & add-on fields (in order)')}</SubLabel>
                    <ChipsBox>
                        {metaItems.map(it => (
                            <ChipToggle
                                key={it.key}
                                label={tr(it.i18nKey, it.fallback)}
                                onRemove={() => setPlacement(it.key, 'available')}
                            />
                        ))}
                    </ChipsBox>
                    {available.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                            {available.map(it => (
                                <AvailableChip
                                    key={`meta-${it.key}`}
                                    label={tr(it.i18nKey, it.fallback)}
                                    onAdd={() => setPlacement(it.key, 'meta')}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Stack>
        </ConfigCard>
    );
};

DescriptionTextsPerLanguageCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default DescriptionTextsPerLanguageCard;
